import axios from "axios";

export interface LinkShopifyRequest {
  shopifySessionToken: string;
}

interface LinkShopifyResponse {
  success: boolean;
  companyName?: string;
  error?: string;
  message?: string;
}

export interface LinkShopifyResult {
  status: number;
  body: LinkShopifyResponse;
}

/**
 * Custom Dashboard-side wrapper for the Shopify embedded-app `/link` endpoint. Same shape as
 * `AuthorizationApi2` and friends — we don't go through the generated OpenAPI client because
 * this single endpoint isn't worth modelling in the spec yet, and the request shape is tiny.
 *
 * Returns the raw HTTP status alongside the parsed body so the caller can distinguish
 * 200 / 409 (conflict) / 422 (ccs_disabled) / 5xx without re-parsing the body's `error`
 * discriminator. 403 `not_approved` is gone as of ENG-6707 — a merchant no longer needs an ops
 * approval to link, so there is nothing left to refuse them for. `validateStatus: () => true` keeps axios from throwing on
 * non-2xx; the caller checks the result.status field instead.
 */
export class ShopifyAdminApi {
  // Explicit fields instead of constructor parameter properties: the latter emit runtime code and
  // are rejected under `erasableSyntaxOnly` (adopted in #4246, after this file landed).
  private readonly baseUrl: string;
  private readonly accessToken: string;

  constructor(baseUrl: string, accessToken: string) {
    this.baseUrl = baseUrl;
    this.accessToken = accessToken;
  }

  async postLink(request: LinkShopifyRequest): Promise<LinkShopifyResult> {
    const response = await axios.post(
      `${this.baseUrl}/integrations/shopify/admin-api/link`,
      request,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          "Content-Type": "application/json",
        },
        validateStatus: () => true,
      },
    );
    return {
      status: response.status,
      body: response.data as LinkShopifyResponse,
    };
  }
}
